import { enqueueSnackbar } from 'notistack';
import { useRef, useMemo, Activity, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  IconEye,
  IconPlus,
  IconBook,
  IconTrash,
  IconBook2,
  IconCheck,
  IconXboxX,
  IconSearch,
  IconLockOpen,
  IconAdjustments,
  IconProgressCheck,
} from '@tabler/icons-react';

import ActionDropdown from '@/components/ActionDropdown';
import Button from '@/components/Button/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import CustomImage from '@/components/CustomImage';
import Input from '@/components/Input/Input';
import ProgressBar from '@/components/ProgressBar';
import ListSummaryCardsSkeleton from '@/components/SkeletonLoader/ListSummaryCardsSkeleton.tsx';
import { SummaryCard } from '@/components/SummaryCard';
import CustomTable from '@/components/Table';
import TagCapsule from '@/components/TagCapsule';
import { LessonStatusConfig } from '@/constants/constant';
import { EBlockUnblock, ELessonStatus } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import {
  removeTeacherLesson,
  getTeacherLessonList,
  getTeacherLessonSummary,
} from '@/service/lessonService';
import { blockUnblockUser } from '@/service/teacherService';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import { ISummaryConfig } from '@/types';
import { IAssignedClass, ITeacherLessonList } from '@/types/lesson';
import { formatLastActive } from '@/utils';

import FilterModal, { ILessonFilters } from './components/FilterModal';

const LESSON_SUMMARY_CONFIG: ISummaryConfig[] = [
  {
    key: 'total_lessons',
    label: 'lessons.total-lessons-created',
    icon: <IconBook2 color="#717784" />,
    value: '0',
  },
  {
    key: 'active_lessons',
    label: 'lessons.active-lessons',
    icon: <IconBook color="#717784" />,
    value: '0',
  },
  {
    key: 'lessons_completed',
    label: 'lessons.lessons-completed',
    icon: <IconCheck color="#717784" />,
    value: '0',
  },
  {
    key: 'avg_completion_rate',
    label: 'lessons.avg-completion-rate',
    icon: <IconProgressCheck color="#717784" />,
    value: '0%',
  },
];

const buildLessonQuery = (
  currentPage: number,
  debouncedSearch: string,
  sortField: string | undefined,
  sortDirection: 'ASC' | 'DESC' | '',
  filters: ILessonFilters | null
): Record<string, unknown> => {
  const query: Record<string, unknown> = {
    limit: 10,
    offset: (currentPage - 1) * 10,
  };

  if (debouncedSearch) query.search = debouncedSearch;
  if (sortField && sortDirection) {
    query[`order[${sortField}]`] = sortDirection;
  } else {
    query[`order[created_at]`] = 'DESC';
  }
  if (filters?.class) query.class_id = filters.class;
  if (filters?.assignedDate) query.assign_date = filters.assignedDate;
  if (filters?.dueDate) query.due_date = filters.dueDate;
  if (filters?.status) query.status = filters.status;

  return query;
};

const TeacherLessons = () => {
  const { formatText } = useFormatText();
  const navigate = useNavigate();
  const { setTitle } = usePageTitle();
  const { isTableLoading, setTableLoading, isScreenLoading, setScreenLoading } =
    useSkeletonStore();

  const [lessons, setLessons] = useState<ITeacherLessonList[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<string | undefined>();
  const [sortDirection, setSortDirection] = useState<'' | 'ASC' | 'DESC'>('');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState<ILessonFilters | null>(null);
  const [statsData, setStatsData] = useState<ISummaryConfig[]>(
    LESSON_SUMMARY_CONFIG
  );
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    lesson: ITeacherLessonList | null;
  }>({ open: false, lesson: null });

  const [blockModal, setBlockModal] = useState<{
    open: boolean;
    lesson: ITeacherLessonList | null;
  }>({ open: false, lesson: null });

  const isInitialLoad = useRef(true);
  const pageTitle = formatText('lessons.title');

  useEffect(() => {
    setTitle(pageTitle);
    return () => {
      setTitle('');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTitle]);

  const { register, watch } = useForm({
    defaultValues: {
      search: '',
    },
  });

  const search = watch('search');
  const debouncedSearch = useDebounce(search, 500);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const setLoadingState = (loading: boolean) => {
    if (isInitialLoad.current) {
      setScreenLoading(loading);
    } else {
      setTableLoading(loading);
    }
  };

  const fetchLessons = async () => {
    setLoadingState(true);

    try {
      const query = buildLessonQuery(
        currentPage,
        debouncedSearch,
        sortField,
        sortDirection,
        filters
      );

      const response = await getTeacherLessonList(query);

      if (response?.status) {
        setLessons(response.data ?? []);
        setTotal(response.total ?? 0);
      } else {
        setLessons([]);
        setTotal(0);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingState(false);
      isInitialLoad.current = false;
    }
  };

  const handleLessonStatus = async (targetLesson?: ITeacherLessonList) => {
    const lesson = targetLesson || blockModal.lesson;
    if (!lesson) return;

    const isCurrentlyInactive = lesson.status === ELessonStatus.IN_ACTIVE;

    try {
      const response = await blockUnblockUser(lesson.id, EBlockUnblock.LESSON, {
        is_block: !isCurrentlyInactive,
      });

      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        if (!targetLesson) {
          setBlockModal({ open: false, lesson: null });
        }
        fetchLessons();
        fetchSummary();
      } else {
        enqueueSnackbar(response?.message, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleRemove = async () => {
    if (!confirmModal.lesson) return;
    try {
      const response = await removeTeacherLesson(confirmModal.lesson.id);

      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        setConfirmModal({ open: false, lesson: null });
        fetchLessons();
        fetchSummary(); // Update summary if deleted
      } else {
        console.error(response?.message);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await getTeacherLessonSummary();
      if (response?.status && response?.data) {
        const apiData = response.data;
        const mappedSummary = LESSON_SUMMARY_CONFIG.map((item) => {
          const value = apiData[item.key];
          return {
            ...item,
            value:
              item.key === 'avg_completion_rate'
                ? `${value ?? 0}%`
                : (value ?? 0),
          };
        });
        setStatsData(mappedSummary);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchLessons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, sortField, sortDirection, filters]);

  useEffect(() => {
    fetchSummary();
  }, []);

  const handleSort = (key: string) => {
    if (sortField === key) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC');
      } else if (sortDirection === 'DESC') {
        setSortField('');
        setSortDirection('');
      } else {
        setSortDirection('ASC');
      }
    } else {
      setSortField(key);
      setSortDirection('ASC');
    }
  };
  const handleApplyFilters = (newFilters: ILessonFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleRowViewClick = (id: string) => {
    navigate(`/teacher/lessons/${id}`);
  };

  const isFilterApplied =
    !!filters?.status ||
    !!(Array.isArray(filters?.class)
      ? filters.class.length > 0
      : !!filters?.class) ||
    !!filters?.assignedDate ||
    !!filters?.dueDate;

  const columns = useMemo(
    () => [
      {
        key: 'title',
        title: 'lessons.columns.lesson-name',
        sorter: true,
        handleSort: () => handleSort('title'),
        render: (record: ITeacherLessonList) => (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-neutral-100 overflow-hidden shrink-0">
              <CustomImage
                src="/assets/images/lesson.jpg"
                alt={record?.title}
                className="w-full h-full object-cover"
              />
            </div>
            <span className="text-neutral-primary font-medium text-sm">
              {record?.title}
            </span>
          </div>
        ),
      },
      {
        key: 'class_assigned',
        title: 'lessons.columns.class-assigned',
        sorter: true,
        handleSort: () => handleSort('class_assigned'),
        render: (record: ITeacherLessonList) => (
          <span>
            {record?.class_assigned
              ?.map((c: IAssignedClass) => c?.name)
              .join(', ')}
          </span>
        ),
      },
      {
        key: 'assign_date',
        title: 'lessons.columns.assigned-date',
        sorter: true,
        handleSort: () => handleSort('assign_date'),
        render: (record: ITeacherLessonList) => (
          <span className="text-neutral-secondary text-sm">
            {new Date(record.assign_date * 1000).toLocaleDateString('en-US', {
              month: 'short',
              day: '2-digit',
              year: 'numeric',
            })}
          </span>
        ),
      },
      {
        key: 'due_date',
        title: 'lessons.columns.due-date',
        sorter: true,
        handleSort: () => handleSort('due_date'),
        render: (record: ITeacherLessonList) => (
          <span className="text-neutral-secondary text-sm">
            {new Date(record.due_date * 1000).toLocaleDateString('en-US', {
              month: 'short',
              day: '2-digit',
              year: 'numeric',
            })}
          </span>
        ),
      },
      {
        key: 'progress',
        title: 'lessons.columns.progress',
        sorter: true,
        handleSort: () => handleSort('progress'),
        render: (record: ITeacherLessonList) => (
          <div className="w-full max-w-37.5">
            <ProgressBar value={record.progress ?? 0} />
          </div>
        ),
      },
      {
        key: 'status',
        title: 'lessons.columns.status',
        sorter: true,
        handleSort: () => handleSort('status'),
        render: (record: ITeacherLessonList) => {
          const config = record?.status
            ? LessonStatusConfig[record?.status as ELessonStatus]
            : null;
          return (
            <TagCapsule
              value={config?.label ? formatText(config.label) : ''}
              className={config?.className}
            />
          );
        },
      },
      {
        key: 'last_update',
        title: 'lessons.columns.last-update',
        sorter: true,
        handleSort: () => handleSort('last_update'),
        render: (record: ITeacherLessonList) => (
          <span className="text-neutral-secondary text-sm">
            {formatLastActive(record?.last_update, formatText)}
          </span>
        ),
      },
      {
        key: 'action',
        title: 'lessons.columns.action',
        align: 'center' as const,
        render: (record: ITeacherLessonList) => (
          <ActionDropdown
            actions={[
              {
                key: 'view',
                label: 'common.actions.view',
                icon: <IconEye size={18} />,
                onClick: () => handleRowViewClick(record?.id),
              },
              {
                key: 'block',
                label:
                  record?.status === ELessonStatus.IN_ACTIVE
                    ? 'common.actions.unblock'
                    : 'common.actions.block',
                icon:
                  record?.status === ELessonStatus.IN_ACTIVE ? (
                    <IconLockOpen size={18} />
                  ) : (
                    <IconXboxX size={18} />
                  ),
                onClick: () =>
                  record?.status === ELessonStatus.IN_ACTIVE
                    ? handleLessonStatus(record)
                    : setBlockModal({ open: true, lesson: record }),
              },
              {
                key: 'remove',
                label: 'common.actions.remove',
                icon: <IconTrash size={18} />,
                isDanger: true,
                onClick: () => setConfirmModal({ open: true, lesson: record }),
              },
            ]}
          />
        ),
      },
    ],
    [lessons, formatText] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const showListUI =
    isScreenLoading ||
    lessons.length > 0 ||
    search.length > 0 ||
    !!debouncedSearch ||
    isFilterApplied;

  const isEmptySearch =
    !isScreenLoading &&
    !isTableLoading &&
    lessons.length === 0 &&
    (!!debouncedSearch || isFilterApplied);

  if (isScreenLoading) {
    return <ListSummaryCardsSkeleton columns={columns} />;
  }

  return (
    <div className="flex flex-col gap-6" data-testid="teacher-lessons-page">
      {showListUI && (
        <>
          <div className="flex flex-wrap justify-start items-center gap-2.5 ps-1">
            <div className="flex items-center gap-4 flex-1">
              <Input
                name="search"
                register={register}
                placeholder={formatText(
                  'lessons.teacher-lesson-search-placeholder'
                )}
                leftIcon={{
                  icon: <IconSearch size={20} className="text-neutral-500" />,
                }}
                className="max-w-70"
                inputClassName="py-[13px]"
              />
              <div className="relative">
                <Button
                  variant="outline"
                  label={formatText('common.filter')}
                  icon={<IconAdjustments size={20} />}
                  className={`px-4 h-12 ${
                    isFilterApplied
                      ? 'border-brand-default! text-brand-default!'
                      : ''
                  }`}
                  onClick={() => setIsFilterModalOpen(true)}
                />
                {isFilterApplied && (
                  <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-brand-default border-2 border-white" />
                )}
              </div>
            </div>
            <Button
              variant="primary"
              label={formatText('lessons.create-lesson')}
              className="px-4 h-12 cursor-pointer"
              icon={<IconPlus size={20} />}
              onClick={() => navigate('/teacher/lessons/create')}
              data-testid="create-lesson-button"
            />
          </div>
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
            data-testid="lessons-summary-cards"
          >
            {statsData?.map((stat) => (
              <SummaryCard
                key={stat?.key}
                variant="dashboard"
                label={formatText(stat.label)}
                value={stat.value}
                icon={stat?.icon}
                className="bg-white"
              />
            ))}
          </div>
        </>
      )}

      <CustomTable
        isLoading={isTableLoading}
        columns={columns}
        dataSource={lessons}
        total={total}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pageSize={10}
        fieldName={sortField}
        sortDirection={sortDirection}
        isEmptySearch={isEmptySearch}
        onRowClick={handleRowViewClick}
        isEmpty={!showListUI}
        emptyTitle={formatText('lessons.empty-title')}
        emptyDescription={formatText('lessons.empty-description')}
        emptyButtonText={formatText('lessons.create-lesson')}
        onEmptyBtnClick={() => navigate('/teacher/lessons/create')}
        emptyImgSrc="/assets/svg/lesson-empty.svg"
        emptyButtonIcon={<IconPlus size={20} />}
      />

      <Activity mode={blockModal.open ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={blockModal.open}
          setIsOpen={(open) =>
            setBlockModal({ open, lesson: open ? blockModal.lesson : null })
          }
          title={formatText('lessons.actions.block-lesson-title')}
          descriptionPrefix={formatText('common.confirmation.block-prefix')}
          descriptionSuffix={formatText('lessons.actions.block-lesson-suffix')}
          entityName={blockModal.lesson?.title || ''}
          confirmLabel={formatText('lessons.actions.block-lesson')}
          cancelLabel={formatText('common.actions.cancel')}
          onConfirm={() => handleLessonStatus()}
          isDestructive
        />
      </Activity>

      <Activity mode={confirmModal.open ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={confirmModal.open}
          setIsOpen={(open) =>
            setConfirmModal({ open, lesson: open ? confirmModal.lesson : null })
          }
          title={formatText('lessons.actions.remove-lesson-title')}
          descriptionPrefix={formatText('common.confirmation.remove-prefix')}
          descriptionSuffix={formatText('lessons.actions.remove-lesson-suffix')}
          entityName={confirmModal?.lesson?.title || ''}
          confirmLabel={formatText('lessons.actions.remove-lesson')}
          cancelLabel={formatText('common.actions.cancel')}
          onConfirm={handleRemove}
          isDestructive
        />
      </Activity>

      <Activity mode={isFilterModalOpen ? 'visible' : 'hidden'}>
        <FilterModal
          isOpen={isFilterModalOpen}
          setIsOpen={setIsFilterModalOpen}
          initialFilters={filters}
          onApplyFilters={handleApplyFilters}
        />
      </Activity>
    </div>
  );
};

export default TeacherLessons;
